import { useState } from 'react';

import { exportsUnlocked } from '../domain/gating.js';
import { EXPORT_KINDS } from '../exports/registry.js';
import type { ExportKind } from '../exports/registry.js';
import { downloadData } from './download.js';
import { useOpenLicenseDialog, useProjectState, useStore } from './hooks.js';

/**
 * Export actions, shared by Step 3 (quick actions) and Step 4 (the
 * export center). The store's requestExport is the gate (S3): in
 * trial the buttons show the lock and open the license dialog.
 */
export function ExportButtons({
  kinds,
  detailed = false,
}: {
  kinds: readonly ExportKind[];
  detailed?: boolean;
}) {
  const store = useStore();
  const state = useProjectState();
  const openLicenseDialog = useOpenLicenseDialog();
  const [note, setNote] = useState<string | null>(null);
  const locked = !exportsUnlocked(state.license);
  const infos = EXPORT_KINDS.filter((info) => kinds.includes(info.kind));

  const act = async (kind: ExportKind) => {
    if (locked) {
      openLicenseDialog();
      return;
    }
    const outcome = await store.requestExport(kind);
    if (outcome.status === 'ready') {
      downloadData(
        outcome.artifact.filename,
        outcome.artifact.data,
        outcome.artifact.mimeType,
      );
      setNote(null);
    } else if (outcome.status === 'locked') {
      openLicenseDialog();
    } else if (outcome.status === 'unavailable') {
      // The detail is development plumbing, not product copy; only a
      // dev build shows it (Block 5 fills every generator).
      setNote(
        import.meta.env.DEV
          ? outcome.message
          : 'This export is not available in this build.',
      );
    } else {
      setNote(outcome.message);
    }
  };

  return (
    <div>
      <div className={detailed ? 'export-list' : 'export-quick'}>
        {infos.map((info) => (
          <button
            key={info.kind}
            className="btn"
            style={detailed ? { textAlign: 'left' } : undefined}
            onClick={() => void act(info.kind)}
          >
            {detailed ? (
              <>
                {info.label} {locked && '🔒'}
                <span className="export-detail">{info.detail}</span>
              </>
            ) : (
              <>
                ↓ {info.label} {locked && '🔒'}
              </>
            )}
          </button>
        ))}
      </div>
      {note !== null && <p className="sub">{note}</p>}
    </div>
  );
}
